import React from 'react'
import moment from 'moment'
import {
  Button,
  InputText,
  ListItem,
  InputTextArea,
  InputLabel,
  CheckBox,
  InputRadio,
} from 'components/atoms'

import { Form } from 'components/molecules'
import { agentService, tourService } from 'core/services'
import { IWindow, IPaymentDetail, IUser } from 'static/interfaces'
import I18n, { tJa } from 'core/i18n'
import { replaceVariableForMail } from 'utils/soundoutOrFormalRequest'
import { IBuildedTour, IWeeklyTourGroup } from 'utils/weeklyTypes'
import * as constants from 'static/constants'
import {
  CANCEL_MAIL_TEMPLATES,
  CANCEL_REASON_AGENT_CAUSE,
  CANCEL_REASON_OPTIONS,
  NO_CANCELLATION_FEE_REASONS,
} from 'static/constants_opcalendar'

interface IErrors {
  [key: string]: string | null
}

interface IProps {
  agent: IUser
  guide: IUser
  selectedStartTime: string
  selectedTour: any
  selectedDate: moment.Moment
  tourGuideId: string
  tour: IBuildedTour
  tourGroup?: IWeeklyTourGroup
  paymentDetail?: IPaymentDetail
  travelCost?: number
  includeTravelCost?: boolean
  cancellationFee?: number
  cancelReason?: string
  close(): void
}

interface IMessageFormProps extends IProps {
  // 親(ラジオ側)が「デフォルト文面から編集済みか」を同期判定するための関数を受け渡す ref
  isEditedRef: React.MutableRefObject<() => boolean>
  // 取得前の初期値(0円)のまま送信して支払明細が0円で確定しないよう、取得完了まで送信を止める
  isCancellationFeeLoaded: boolean
}
declare var window: IWindow

const FIELDS = {
  subject: 'subject',
  message: 'message',
}

// TODO 打診チャット対応後催行時間を追加対応
const MessageForm: React.FC<IMessageFormProps> = ({
  agent,
  guide,
  selectedStartTime,
  selectedDate,
  selectedTour,
  tourGuideId,
  tour,
  tourGroup,
  close,
  travelCost = 0,
  includeTravelCost,
  cancellationFee = 0,
  cancelReason = '',
  isEditedRef,
  isCancellationFeeLoaded,
}) => {
  const [sending, setSending] = React.useState(false)
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(true)
  const [errors, setErrors] = React.useState<IErrors>({})

  const template = CANCEL_MAIL_TEMPLATES[cancelReason]

  const defaultSubject = React.useMemo(
    () => replaceVariableForMail(template.subject, tour, guide, agent, tourGroup),
    [template, tour, guide, agent, tourGroup]
  )

  const defaultMessage = React.useMemo(() => {
    let message = replaceVariableForMail(template.message, tour, guide, agent, tourGroup).replace(
      /\[cancellation_fee\]/g,
      cancellationFee.toLocaleString()
    )
    if (includeTravelCost) {
      // 「キャンセル料　○○円」の直下に追加する。見つからない場合は末尾に追加する
      const feeLine = `キャンセル料　${cancellationFee.toLocaleString()}円`
      const travelCostLine = `交通費　${travelCost.toLocaleString()}円`
      message = message.includes(feeLine)
        ? message.replace(feeLine, `${feeLine}\n${travelCostLine}`)
        : `${message}\n\n${travelCostLine}`
    }
    return message
  }, [includeTravelCost, travelCost, template, cancellationFee, tour, guide, agent, tourGroup])

  // 現在の入力値。デフォルト文面との比較で「編集済みか」を判定するために持つ
  const valuesRef = React.useRef({ subject: defaultSubject, message: defaultMessage })

  // InputText/InputTextArea は props.value の変化に追従して入力値を上書きするため(料金取得完了・
  // 交通費トグル時など)、デフォルトが変わったら比較対象の現在値もフィールド単位で同期する
  React.useEffect(() => {
    valuesRef.current.subject = defaultSubject
  }, [defaultSubject])
  React.useEffect(() => {
    valuesRef.current.message = defaultMessage
  }, [defaultMessage])

  React.useEffect(() => {
    isEditedRef.current = () =>
      valuesRef.current.subject !== defaultSubject || valuesRef.current.message !== defaultMessage
  }, [defaultSubject, defaultMessage])

  const handleSubmit = React.useCallback(
    async (_, values) => {
      setSending(true)
      const formal_request = {
        tour_attributes: {
          start_time: selectedStartTime,
          day: selectedDate,
          post_id: selectedTour.value ?? selectedTour.post_id, // 選択による場合と直接Tourが渡される場合があるため
        },
        tour_guide_attributes: {
          id: tourGuideId,
          guide_id: guide.id.toString(),
          request_status: 'agent_canceled',
          cancel_reason: cancelReason,
        },
        optional_attributes: {
          subject: values.subject,
          travel_cost: includeTravelCost ? travelCost : 0,
          // メール本文に記載した額と支払明細を一致させるため、常に表示中の額を送る
          cancellation_fee: cancellationFee,
        },
      }
      try {
        await agentService.tourGuideCancel(tourGuideId, {
          formal_request,
          mail_body: values.message,
        })
        window.flashMessages.addMessage({ text: I18n.t('generic.send_success') })
        close()
      } catch (e) {
        console.error(e)
      } finally {
        setSending(false)
      }
    },
    [
      guide,
      selectedTour,
      selectedStartTime,
      selectedDate,
      includeTravelCost,
      travelCost,
      cancellationFee,
      cancelReason,
    ]
  )

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  return (
    <Form fields={FIELDS} handleSubmit={handleSubmit} handleUpdateForm={handleUpdateForm}>
      <ListItem>
        <InputText
          name="subject"
          label="タイトル"
          value={defaultSubject}
          error={errors.subject}
          required={true}
          onChangeHandler={e => (valuesRef.current.subject = e.target.value)}
        />
      </ListItem>
      <ListItem>
        <InputTextArea
          name="message"
          label="本文"
          value={defaultMessage}
          error={errors.message}
          required={true}
          onChangeHandler={e => (valuesRef.current.message = e.target.value)}
        />
      </ListItem>
      <ListItem>
        <Button
          primary={true}
          disabled={!isSubmitEnabled || sending || !cancelReason || !isCancellationFeeLoaded}
        >
          メッセージを送信
        </Button>
      </ListItem>
    </Form>
  )
}

export const CancelFormalRequestsModalBody: React.FC<IProps> = props => {
  const { tour, tourGuideId, paymentDetail } = props
  const [includeTravelCost, setIncludeTravelCost] = React.useState(false)
  const [travelCost, setTravelCost] = React.useState<number>(paymentDetail?.travel_cost || 0)
  const [guideCancellationFee, setGuideCancellationFee] = React.useState(0)
  const [isCancellationFeeLoaded, setIsCancellationFeeLoaded] = React.useState(false)
  // キャンセル料を手入力するか。手入力時は manualCancellationFee を使い、それ以外は自動計算値を使う
  const [isManualCancellationFee, setIsManualCancellationFee] = React.useState(false)
  const [manualCancellationFee, setManualCancellationFee] = React.useState(0)
  // 入力中のキャンセル料。本文の再生成(=手編集の上書き)はフォーカスを外して確定したときだけ行う
  const [manualCancellationFeeDraft, setManualCancellationFeeDraft] = React.useState('')
  const [cancelReason, setCancelReason] = React.useState<string>(CANCEL_REASON_AGENT_CAUSE)
  const [radioResetKey, setRadioResetKey] = React.useState(0)
  const isEditedRef = React.useRef<() => boolean>(() => false)
  React.useEffect(() => {
    tourService
      .getGuideCancellationFee(tour.id, tourGuideId)
      .then(data => {
        setGuideCancellationFee(data.guide_cancellation_fee)
      })
      // 取得失敗時は従来どおり0円表示のまま送信できるようにする(永久に送信不可にしない)
      .finally(() => setIsCancellationFeeLoaded(true))
  }, [tour.id, tourGuideId])

  const isNoCancellationFee = NO_CANCELLATION_FEE_REASONS.includes(cancelReason)
  const autoCancellationFee = isNoCancellationFee ? 0 : guideCancellationFee
  const cancellationFee = isManualCancellationFee ? manualCancellationFee : autoCancellationFee

  const cancelReasonRadioOptions = React.useMemo(
    () =>
      CANCEL_REASON_OPTIONS.map(opt => ({
        id: opt.id,
        value: opt.value,
        label: tJa(opt.labelKey),
      })),
    []
  )

  // 件名・本文を手編集済みなら、デフォルト文面の再生成で消えることを確認してもらう
  const confirmDiscardEdits = () =>
    !isEditedRef.current() || window.confirm('編集内容がリセットされます。よろしいですか？')

  const handleCancelReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!confirmDiscardEdits()) {
      // InputRadio は onChangeHandler より先に内部 state を更新してしまうため、
      // key を変えて再マウントし元の選択に巻き戻す
      setRadioResetKey(key => key + 1)
      return
    }
    setCancelReason(e.target.value)
    // 理由ごとに自動計算値が変わるため、手入力したキャンセル料は自動計算に戻す
    setIsManualCancellationFee(false)
    setManualCancellationFee(0)
    setManualCancellationFeeDraft('')
    // 交通費チェックボックスはエージェント起因のときのみ表示するため、他の理由へ切替時はリセットする
    if (e.target.value !== CANCEL_REASON_AGENT_CAUSE) {
      setIncludeTravelCost(false)
      setTravelCost(0)
    }
  }

  const handleManualCancellationFeeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    // チェック時は自動計算値を初期値にするため、本文が変わる(=確認が必要)のは手入力値を解除するときだけ。
    // CheckBox は制御コンポーネントなので、state を変えなければチェック状態は元に戻る
    if (cancellationFee !== autoCancellationFee && !confirmDiscardEdits()) {
      return
    }
    setIsManualCancellationFee(e.target.checked)
    const fee = e.target.checked ? autoCancellationFee : 0
    setManualCancellationFee(fee)
    setManualCancellationFeeDraft(String(fee))
  }

  const commitManualCancellationFee = () => {
    const fee = Math.max(0, Number(manualCancellationFeeDraft) || 0)
    if (fee !== manualCancellationFee && !confirmDiscardEdits()) {
      setManualCancellationFeeDraft(String(manualCancellationFee))
      return
    }
    setManualCancellationFee(fee)
    setManualCancellationFeeDraft(String(fee))
  }

  return (
    <>
      <ListItem>
        <InputText name="guide_name" label="ガイド" value={props.guide.username} readonly={true} />
      </ListItem>
      <ListItem>
        <InputRadio
          key={radioResetKey}
          name="cancel_reason"
          label="キャンセル理由"
          options={cancelReasonRadioOptions}
          defaultValue={cancelReason}
          required={true}
          onChangeHandler={handleCancelReasonChange}
        />
      </ListItem>
      <ListItem>
        {/* 「キャンセル料 ¥xxx □手入力する」を1行に並べ、チェック時は金額表示が入力欄に切り替わる */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <InputLabel label="キャンセル料" />
          {isManualCancellationFee ? (
            // InputLabel の右パディングに続けて配置し、他の入力欄と左端を揃える
            <div style={{ width: '160px', marginRight: '12px' }}>
              <InputText
                required={true}
                type="number"
                name="cancellation_fee"
                value={manualCancellationFeeDraft}
                onChangeHandler={e => setManualCancellationFeeDraft(e.target.value)}
                onBlurHandler={commitManualCancellationFee}
              />
            </div>
          ) : (
            <div
              style={{
                color: constants.COLORS.cautionText,
                fontWeight: 'bold',
                marginRight: '12px',
              }}
            >
              ¥{cancellationFee.toLocaleString()}
            </div>
          )}
          <CheckBox
            name="is_manual_cancellation_fee"
            showLabelInline={true}
            checked={isManualCancellationFee}
            onChangeHandler={handleManualCancellationFeeToggle}
            label="手入力する"
          />
        </div>
      </ListItem>
      {cancelReason === CANCEL_REASON_AGENT_CAUSE &&
        moment(tour.day).isSameOrBefore(moment(), 'day') && (
          <>
            <ListItem>
              <CheckBox
                name="include_travel_cost"
                checked={includeTravelCost}
                onChangeHandler={e => {
                  setIncludeTravelCost(e.target.checked)
                  if (!e.target.checked) {
                    setTravelCost(0)
                  }
                }}
                label="交通費を含める"
              />
            </ListItem>
            {includeTravelCost && (
              <ListItem>
                <InputText
                  required={true}
                  type="number"
                  name="travel_cost"
                  label="交通費"
                  value={travelCost}
                  onChangeHandler={e => setTravelCost(Number(e.target.value) || 0)}
                />
              </ListItem>
            )}
          </>
        )}
      <ListItem>
        {/* key により理由切替時に再マウントし、件名・本文をデフォルトへ確実にリセットする
            (件名テンプレートが理由間で同一文字列のとき value の変化だけではリセットされないため) */}
        <MessageForm
          {...props}
          key={cancelReason}
          travelCost={travelCost}
          includeTravelCost={includeTravelCost}
          cancellationFee={cancellationFee}
          cancelReason={cancelReason}
          isEditedRef={isEditedRef}
          isCancellationFeeLoaded={isCancellationFeeLoaded}
        />
      </ListItem>
    </>
  )
}
